import React, { useEffect, useCallback, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { useCallStore } from './store/useCallStore';
import { useAuthStore } from './store/useAuthStore';
import { fetchStatuses } from './api/status';
import { fetchMe, logoutApi, refreshToken } from './api/auth';
import { useStatusSocket } from './hooks/useStatusSocket';
import { useInactivityTimer } from './hooks/useInactivityTimer';
import { applyCompanyDefaults } from './utils/applyCompanyDefaults';
import LoginPage from './components/LoginPage';
import CallHistoryView from './components/CallHistoryView';
import MiniView from './components/MiniView';
import AdminPage from './components/admin/AdminPage';
import ChangePasswordModal from './components/ChangePasswordModal';
import ToastContainer from './components/ToastContainer';

// App-Shell: Auth-Bootstrapping, WebSocket, Sitzungs-Countdown und Hash-Routing.
// #/admin rendert die Admin-Seite (Anrufliste samt Auto-Refresh dort unmounted),
// alles andere die Anrufhistorie.

export default function App() {
  const { setCalls, applyStatuses, setUserName } = useCallStore();
  const { token, user, login, logout, tokenExpiresAt } = useAuthStore();
  const [bootChecked, setBootChecked] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [msToExpiry, setMsToExpiry] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [route, setRoute] = useState(window.location.hash);
  const darkMode = useCallStore((s) => s.darkMode);

  // Hash-Routing (ohne Router-Dependency)
  useEffect(() => {
    const handler = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', handler);
    return () => window.removeEventListener('hashchange', handler);
  }, []);

  // Apply dark mode to the document root
  useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  // On mount: if token exists, verify it and load user/company
  useEffect(() => {
    if (!token) { setBootChecked(true); return; }
    fetchMe()
      .then(({ user: u, company: c }) => {
        login(token, u, c);
        setUserName(u.displayName);
        // Auch nach einem Reload gelten die Firmen-Vorgaben (Zeitraum, Spalten, …)
        applyCompanyDefaults(c);
      })
      .catch(() => { logout(); })
      .finally(() => setBootChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const syncStatuses = useCallback(async () => {
    try {
      const statuses = await fetchStatuses();
      applyStatuses(statuses);
    } catch {
      // silently ignore
    }
  }, [applyStatuses]);

  useStatusSocket(syncStatuses);

  const handleLogout = useCallback(() => {
    logoutApi().catch(() => { /* session may already be gone */ }).finally(() => {
      setCalls([]);
      logout();
      window.location.hash = '';
    });
  }, [logout, setCalls]);

  // Admins are logged out after 30 min of inactivity (never persisted)
  useInactivityTimer(30 * 60 * 1000, handleLogout, user?.role === 'admin');

  // Session expiry countdown — update every 30 s
  useEffect(() => {
    if (!tokenExpiresAt || !user) { setMsToExpiry(null); return; }
    const tick = () => setMsToExpiry(Math.max(0, tokenExpiresAt - Date.now()));
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [tokenExpiresAt, user]);

  const handleRefreshToken = useCallback(async () => {
    setRefreshing(true);
    try {
      const { token: newToken, user: newUser, company: newCompany } = await refreshToken();
      login(newToken, newUser, newCompany);
    } catch { handleLogout(); }
    finally { setRefreshing(false); }
  }, [login, handleLogout]);

  // Show spinner while verifying token
  if (!bootChecked) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center">
        <span className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  if (!user) return <LoginPage />;

  const isAdminRoute = route.startsWith('#/admin') && user.role === 'admin';
  const isMiniRoute = route.startsWith('#/mini');

  // Popup-Fallback der Mini-Ansicht (Browser ohne Document-PiP): nur die Live-Liste.
  if (isMiniRoute) return <MiniView />;

  return (
    <>
      <ToastContainer />
      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}

      {isAdminRoute ? (
        <AdminPage
          onLogout={handleLogout}
          onChangePassword={() => setShowChangePassword(true)}
        />
      ) : (
        <CallHistoryView
          onLogout={handleLogout}
          onChangePassword={() => setShowChangePassword(true)}
          msToExpiry={msToExpiry}
          refreshing={refreshing}
          onRefreshToken={handleRefreshToken}
        />
      )}
    </>
  );
}
